import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { CmApiHttpError } from '@/api/cmApi'
import {
  isCreditLimitLogsOk,
  searchCreditLimitLogs,
  searchCreditLimitLogsBQ,
  toCreditLimitTransferResult,
} from '@/api/map/creditLimitLogs'
import type {
  CreditLimitTransferQuery,
  CreditLimitTransferResult,
} from './clTransfersTypes'

/** Postgres = today live log; BQ = historical CL Transfers (Lab Player P/L routing). */
export type CreditLimitLogSource = 'postgres' | 'bq'

/** Live empty when CM-API credit-limit log is unreachable. */
export function unavailableCreditLimitTransfers(
  query: CreditLimitTransferQuery = {},
  reason?: string,
  trackedBy: string = 'cm-ui-logs:cl-transfers',
): CreditLimitTransferResult {
  const pageSize = Math.max(1, query.pageSize ?? DEFAULT_PAGE_SIZE)
  return {
    rows: [],
    total: 0,
    page: 1,
    pageSize,
    unavailable: {
      code: 'no_credit_limit_log_source',
      reason:
        reason ??
        'Credit-limit transfer history needs CM-API credit-limit logs. None is reachable, so this console will not invent balances, limits, or IPs.',
      trackedBy,
    },
  }
}

/**
 * Live Keycloak → CM-API credit-limit log (Standard envelope → display).
 * `postgres` = searchCreditLimitLogs; `bq` = searchCreditLimitLogsBQ.
 * Unreachable / non-200 → unavailable (never invents money rows).
 */
export async function loadCreditLimitTransfers(
  query: CreditLimitTransferQuery = {},
  currency?: string | null,
  source: CreditLimitLogSource = 'postgres',
): Promise<CreditLimitTransferResult> {
  const trackedBy =
    source === 'bq' ? 'cm-ui-logs:cl-transfers-bq' : 'cm-ui-logs:cl-transfers'
  const search = source === 'bq' ? searchCreditLimitLogsBQ : searchCreditLimitLogs
  try {
    const live = await search({
      page: query.page ?? 1,
      pageSize: query.pageSize ?? DEFAULT_PAGE_SIZE,
      ...(query.fromDate ? { fromDate: query.fromDate } : {}),
      ...(query.toDate ? { toDate: query.toDate } : {}),
    })
    if (!isCreditLimitLogsOk(live)) {
      return unavailableCreditLimitTransfers(
        query,
        'Credit-limit log returned no data.',
        trackedBy,
      )
    }
    return toCreditLimitTransferResult(live, currency)
  } catch (err) {
    const status = err instanceof CmApiHttpError ? err.status : 0
    console.warn(`[cl-transfers] CM-API credit-limit log (${source}) unavailable`, err)
    return unavailableCreditLimitTransfers(
      query,
      status === 404
        ? source === 'bq'
          ? 'CM-API searchCreditLimitLogsBQ route is not deployed yet.'
          : 'CM-API credit-limit log route is not deployed yet.'
        : undefined,
      trackedBy,
    )
  }
}
